import authService from './authService';
import type {
  Process,
  ProcessExecution,
  ProcessAction,
  ProcessUpdatePayload,
  ProcessRunPayload,
} from '../types/process';

// Client for the process execution framework endpoints (Configuração › Operações).
class ProcessService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async unwrap<T>(response: Response, errMsg: string): Promise<T> {
    if (!response.ok) {
      let detail = '';
      try {
        const body = await response.json();
        detail = body?.message || body?.error || '';
      } catch {
        /* ignore */
      }
      throw new Error(detail || errMsg);
    }
    const json = await response.json();
    return json.data as T;
  }

  async listProcesses(): Promise<Process[]> {
    const api = await this.getApiClient();
    const res = await api.get('/processes');
    return (await this.unwrap<Process[]>(res, 'Falha ao listar processos')) ?? [];
  }

  async updateProcess(id: number, payload: ProcessUpdatePayload): Promise<Process> {
    const api = await this.getApiClient();
    const res = await api.put(`/processes/${id}`, payload);
    return this.unwrap<Process>(res, 'Falha ao atualizar processo');
  }

  async listExecutions(processId: number, limit = 50): Promise<ProcessExecution[]> {
    const api = await this.getApiClient();
    const res = await api.get(`/processes/${processId}/executions?limit=${limit}`);
    return (await this.unwrap<ProcessExecution[]>(res, 'Falha ao listar execuções')) ?? [];
  }

  async runProcess(id: number, payload?: ProcessRunPayload): Promise<ProcessExecution> {
    const api = await this.getApiClient();
    // Body is optional: a plain run posts nothing; processes with custom inputs
    // (e.g. bi_daily's reprocessDates) pass a payload.
    const res = await api.post(`/processes/${id}/run`, payload);
    return this.unwrap<ProcessExecution>(res, 'Falha ao executar processo');
  }

  async preExecute(id: number): Promise<{ execution: ProcessExecution; actions: ProcessAction[] }> {
    const api = await this.getApiClient();
    const res = await api.post(`/processes/${id}/preexecute`);
    const data = await this.unwrap<{ execution: ProcessExecution; actions: ProcessAction[] }>(
      res,
      'Falha ao pré-executar processo',
    );
    return { execution: data.execution, actions: data.actions ?? [] };
  }

  async listActions(executionId: number): Promise<ProcessAction[]> {
    const api = await this.getApiClient();
    const res = await api.get(`/process-executions/${executionId}/actions`);
    return (await this.unwrap<ProcessAction[]>(res, 'Falha ao listar ações')) ?? [];
  }

  async approve(executionId: number, approvedActionIds: number[]): Promise<ProcessExecution> {
    const api = await this.getApiClient();
    const res = await api.post(`/process-executions/${executionId}/approve`, {
      approvedActionIds,
    });
    return this.unwrap<ProcessExecution>(res, 'Falha ao aprovar execução');
  }

  async cancel(executionId: number): Promise<ProcessExecution> {
    const api = await this.getApiClient();
    const res = await api.post(`/process-executions/${executionId}/cancel`);
    return this.unwrap<ProcessExecution>(res, 'Falha ao cancelar execução');
  }
}

export const processService = new ProcessService();
export default processService;
